import React from 'react'
import styled from 'styled-components'
import {
  Box,
  Button,
  get,
  ScreenReaderOnly,
  TextBody,
} from '@ally/metronome-ui'
import { toKebabCase } from '@ally/utilitarian'
import ReorderButtons from './ReorderButtons'
import { AccountTypeItem, LobTypeItem, WidgetTypeItem } from './Templates'
import {
  DataTypeName,
  DataType,
  AccountTypesObj,
  WidgetKey,
} from '../../../types'
import { useCustomizeDashboard } from '../../../customizationContext/CustomizeDashboardContext'
import { reorderListItemLabel } from '../../../constants'
import { setFocus } from '../../../utils'

export const List = styled(Box).attrs({ role: 'list' })``

export const ListItem = styled(Box).attrs({
  role: 'listitem',
})``

const ListItemGroup = styled(Box)`
  border-bottom: 1px solid ${get.colors('slate-2')};
`

const DeleteButton = styled(Button)`
  flex-basis: auto;
  margin-left: ${get.space('md')};
  overflow: visible;
`
export interface RefItem {
  up: React.RefObject<HTMLButtonElement>
  down: React.RefObject<HTMLButtonElement>
}

export interface RefObj {
  [key: string]: RefItem
}

interface ReorderListItem {
  refObj: RefItem
  type: string
  item: DataType<DataTypeName>
  index: number
  moveItem: (indexToMove: number, refKey: string, direction: string) => void
  disabledBtns?: string
}

interface ReorderListProps {
  type: DataTypeName
  list: DataType<DataTypeName>[]
  onListChange: (type: DataTypeName, list: DataType<DataTypeName>[]) => void
}

function increment(
  array: DataType<DataTypeName>[],
  indexToMove: number,
): DataType<DataTypeName>[] {
  const newArray = [...array].map((item, index) => {
    let newOrder: number = item.order
    if (index === indexToMove + 1) {
      newOrder -= 1
    } else if (index === indexToMove) {
      newOrder += 1
    }
    return { ...item, order: newOrder }
  })

  return newArray
}

function decrement(
  array: DataType<DataTypeName>[],
  indexToMove: number,
): DataType<DataTypeName>[] {
  const newArray = [...array].map((item, index) => {
    let newOrder: number = item.order
    if (index === indexToMove - 1) {
      newOrder += 1
    } else if (index === indexToMove) {
      newOrder -= 1
    }
    return { ...item, order: newOrder }
  })

  return newArray
}

function reorder(
  array: DataType<DataTypeName>[],
  indexToMove: number,
  direction: string,
): DataType<DataTypeName>[] {
  const reorderedArray = (): DataType<DataTypeName>[] => {
    switch (direction) {
      case 'up':
        return decrement(array, indexToMove)
      default:
        // 'down'
        return increment(array, indexToMove)
    }
  }

  return reorderedArray().sort((a, b) => a.order - b.order)
}

function getTemplate(
  type: string,
  item: DataType<DataTypeName>,
): React.ReactElement {
  const { name } = item
  switch (type) {
    case 'accountTypes':
      return <AccountTypeItem item={item as AccountTypesObj} />
    case 'lobs':
      return <LobTypeItem name={name} />
    case 'widgets':
    default:
      return <WidgetTypeItem name={name as WidgetKey} />
  }
}

const ReorderListItem: React.FC<ReorderListItem> = ({
  refObj,
  type,
  item,
  index,
  moveItem,
  disabledBtns,
}) => {
  const [{ isSaving }, dispatch] = useCustomizeDashboard()
  const { name } = item
  return (
    <ListItem data-testid={`account-${name}`}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <ListItemGroup
          display="flex"
          justifyContent="space-between"
          alignItems="stretch"
          flexBasis="100%"
        >
          {getTemplate(type, item)}
          <ReorderButtons
            refObj={refObj}
            index={index}
            item={item}
            moveItem={moveItem}
            disabledBtns={disabledBtns}
          />
        </ListItemGroup>
        {type === 'widgets' && (
          <DeleteButton
            variant="link"
            onClick={(): void =>
              dispatch({
                type: 'Delete Widget',
                payload: item.name,
              })
            }
            text="Delete"
            allytmln={`delete ${item.name}`}
            disabled={isSaving}
            aria-disabled={isSaving}
          />
        )}
      </Box>
    </ListItem>
  )
}

const ReorderList: React.FC<ReorderListProps> = ({
  type,
  list: items,
  onListChange,
}) => {
  const [list, setList] = React.useState<DataType<DataTypeName>[]>(items)
  const [itemPositionMessage, setItemPositionMessage] = React.useState('')
  const [elRefs, setElRefs] = React.useState<RefObj>({} as RefObj)
  const [currentFocus, setCurrentFocus] = React.useState<
    React.RefObject<HTMLButtonElement>
  >()
  const [{ isSaving }] = useCustomizeDashboard()

  React.useEffect(() => {
    const refs = items.reduce((acc, item) => {
      return {
        ...acc,
        [toKebabCase(item.name)]: {
          up: React.createRef<HTMLButtonElement>(),
          down: React.createRef<HTMLButtonElement>(),
        },
      }
    }, {})
    setElRefs(refs)
    setList(items)
  }, [items, items.length])

  React.useEffect(() => {
    if (currentFocus) setFocus(currentFocus)
  }, [currentFocus, list])

  const moveItem = (
    indexToMove: number,
    refKey: string,
    direction: string,
  ): void => {
    const movedIndex = direction === 'up' ? indexToMove - 1 : indexToMove + 1
    const newList = reorder(list, indexToMove, direction)

    setItemPositionMessage(
      `${reorderListItemLabel[list[indexToMove].name]} position ${
        movedIndex + 1
      } of ${list.length}`,
    )
    setList(newList)
    setCurrentFocus(elRefs[refKey][direction as keyof RefItem])
    onListChange(type, newList)
  }

  return Object.keys(elRefs).length > 0 ? (
    <Box>
      {itemPositionMessage && (
        <ScreenReaderOnly>
          <TextBody tag="span" size="sm" aria-live="polite">
            {itemPositionMessage}
          </TextBody>
        </ScreenReaderOnly>
      )}
      <List>
        {list.map((item, index, array) => {
          if (array.length === 1 || isSaving) {
            return (
              <ReorderListItem
                key={`${toKebabCase(item.name)}`}
                refObj={elRefs[toKebabCase(item.name)]}
                type={type}
                item={item}
                index={index}
                moveItem={moveItem}
                disabledBtns="both"
              />
            )
          }
          if (array.length > 1 && index === 0) {
            return (
              <ReorderListItem
                key={`${toKebabCase(item.name)}`}
                refObj={elRefs[toKebabCase(item.name)]}
                type={type}
                item={item}
                index={index}
                moveItem={moveItem}
                disabledBtns="first"
              />
            )
          }
          if (array.length > 1 && index === array.length - 1) {
            return (
              <ReorderListItem
                key={`${toKebabCase(item.name)}`}
                refObj={elRefs[toKebabCase(item.name)]}
                type={type}
                item={item}
                index={index}
                moveItem={moveItem}
                disabledBtns="last"
              />
            )
          }
          return (
            <ReorderListItem
              key={`${toKebabCase(item.name)}`}
              refObj={elRefs[toKebabCase(item.name)]}
              type={type}
              item={item}
              index={index}
              moveItem={moveItem}
            />
          )
        })}
      </List>
    </Box>
  ) : null
}

export default ReorderList
